/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Button, Dialog } from '@coursera/cds-core';
import { DownloadIcon, ErrorIcon } from '@coursera/cds-icons';

import StatusCard from 'bundles/enterprise-admin-learners/components/invite-learners/StatusCard';
import { downloadInviteErrorReport } from 'bundles/enterprise-admin-learners/components/invite-learners/downloadInviteErrorReport';
import type { FailedInvite } from 'bundles/enterprise-admin-learners/hooks/useInviteLearners';

import _t from 'i18n!nls/enterprise-admin-learners';

const { HeadingGroup, Content, Actions } = Dialog;

const styles = {
  content: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  downloadButton: css`
    align-self: flex-start;
    padding-left: 0;
  `,
};

export type NoInvitesSentDialogProps = { open: boolean; onClose: () => void; failed: FailedInvite[] };

/** Shown when every invite in the submission failed — no group was assigned. */
const NoInvitesSentDialog = ({ open, onClose, failed }: NoInvitesSentDialogProps) => {
  const failedCount = failed.length;

  return (
    <Dialog open={open} onClose={onClose} width="medium">
      <HeadingGroup>{_t('No invites sent')}</HeadingGroup>
      <Content css={styles.content}>
        {/* Always-present live region so screen readers announce the failure when it appears. */}
        <div role="alert">
          <StatusCard
            title={_t('Skipped')}
            detail={
              failedCount === 1
                ? _t('1 entry had missing or invalid data')
                : _t('#{failedCount} entries had missing or invalid data', { failedCount })
            }
            detailColor="error"
            icon={<ErrorIcon color="error" />}
          />
        </div>
        <Button
          variant="ghost"
          size="small"
          icon={<DownloadIcon />}
          iconPosition="before"
          onClick={() => downloadInviteErrorReport(failed)}
          css={styles.downloadButton}
        >
          {_t('Download error report')}
        </Button>
      </Content>
      <Actions>
        <Button variant="primary" onClick={onClose}>
          {_t('Done')}
        </Button>
      </Actions>
    </Dialog>
  );
};

export default NoInvitesSentDialog;
